import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { handleAxiosError } from '@/shared';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import type {
  PlanComision,
  CommissionTier,
  BonoEscalon,
  ValeRegla,
  ChargebackConfig,
  ChargebackEvent,
  CommissionRun,
  ParametrosComision,
  FiltrosComisionAsesor,
  TaskStatusResponse,
  ComisionAsesorRow,
  ParametroChangeLog,
  ExportRequest,
  JefaturaTramo,
  JefaturaConfig,
  JefaturaAjusteMeta,
  LiderTramo,
  LiderPersona,
  LiderConfig,
  CoordinadorPorcentaje,
  ComisionJefaturaRow,
  ComisionLiderRow,
  ComisionCoordinadorRow,
  ContratoChargeback,
} from '@/lib/commissions/types';

const { get, post, put, remove } = erpApi();

const extractPayload = <T>(resp: any): T => {
  if (resp && typeof resp === 'object') {
    if ('data' in resp && resp.data !== undefined) {
      return resp.data as T;
    }
    if ('results' in resp && Array.isArray(resp.results)) {
      return resp.results as T;
    }
  }
  return resp as T;
};

const extractList = <T>(resp: any): T[] => {
  const payload = extractPayload<any>(resp);
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  if (payload && typeof payload === 'object' && 'results' in payload && Array.isArray((payload as any).results)) {
    return (payload as any).results as T[];
  }
  return [];
};

// ==================== PLANES ====================
export const useFetchPlanes = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'planes'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/planes/`;
      const resp = await get<any>(url, true);
      // Manejar respuesta paginada o array directo
      const planes = extractList<PlanComision>(resp);
      return planes.map((plan) => ({
        ...plan,
        precio_unitario: Number(plan.precio_unitario ?? 0),
        activo: Boolean(plan.activo),
        normalizado: plan.normalizado ?? null,
      }));
    },
    enabled,
  });
};

export const useCreatePlan = (params: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (plan: Partial<PlanComision>) => {
      const { id, ...planData } = plan;

      // Construir payload explícitamente
      const payload: any = {
        code: planData.code,
        nombre: planData.nombre,
        precio_unitario: Number(planData.precio_unitario),
        activo: Boolean(planData.activo),
      };

      // Solo agregar normalizado si existe
      if (planData.normalizado !== null && planData.normalizado !== undefined && planData.normalizado !== '') {
        payload.normalizado = planData.normalizado;
      }

      const url = `${urlBackend}api/v1/comisiones/planes/`;
      const resp = await post<PlanComision>(url, payload, true, true);
      return extractPayload<PlanComision>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'planes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Plan creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdatePlan = (params?: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const enableSuccessAlert = params?.enableSuccessAlert ?? true;
  const successMessage = params?.customSuccessMessage ?? 'Plan actualizado correctamente';

  return useMutation({
    mutationFn: async ({ id, ...plan }: PlanComision) => {
      const url = `${urlBackend}api/v1/comisiones/planes/${id}/`;
      const resp = await put<PlanComision>(url, plan, true, true);
      return extractPayload<PlanComision>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'planes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      if (enableSuccessAlert) {
        setAlert(true, successMessage, AlertSeverityEnum.success);
      }
    },
    onError: error => handleAxiosError(error, params?.customMessageErrorAlert),
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/planes/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'planes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Plan eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== TIERS ====================
export const useFetchTiers = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'tiers'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/tiers/`;
      const resp = await get<any>(url, true);
      const tiers = extractList<CommissionTier>(resp);
      return tiers.map((tier) => ({
        ...tier,
        desde: Number(tier.desde ?? 0),
        hasta: Number(tier.hasta ?? 0),
        factor: Number(tier.factor ?? 0),
        ciudad: tier.ciudad ?? null,
      }));
    },
    enabled,
  });
};

export const useCreateTier = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (tier: Partial<CommissionTier>) => {
      const { id, ...tierData } = tier;

      // Construir payload explícitamente
      const payload: any = {
        rol: tierData.rol,
        desde: Number(tierData.desde),
        hasta: Number(tierData.hasta),
        factor: Number(tierData.factor),
      };

      // Solo agregar ciudad si existe
      if (tierData.ciudad !== null && tierData.ciudad !== undefined && tierData.ciudad !== '') {
        payload.ciudad = tierData.ciudad;
      }

      console.log('Creating tier with data:', payload);

      const url = `${urlBackend}api/v1/comisiones/tiers/`;
      const resp = await post<CommissionTier>(url, payload, true, true);
      return extractPayload<CommissionTier>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'tiers'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Tier creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateTier = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...tier }: CommissionTier) => {
      const url = `${urlBackend}api/v1/comisiones/tiers/${id}/`;
      const resp = await put<CommissionTier>(url, tier, true, true);
      return extractPayload<CommissionTier>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'tiers'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Tier actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteTier = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/tiers/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'tiers'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Tier eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== BONOS ====================
export const useFetchBonos = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'bonos'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/bonos/`;
      const resp = await get<any>(url, true);
      const bonos = extractList<BonoEscalon>(resp);
      return bonos.map((bono) => ({
        ...bono,
        umbral_desde: Number(bono.umbral_desde ?? 0),
        monto: Number(bono.monto ?? 0),
      }));
    },
    enabled,
  });
};

export const useCreateBono = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (bono: Partial<BonoEscalon>) => {
      const { id, ...bonoData } = bono;

      // Construir payload explícitamente
      const payload: any = {
        rol: bonoData.rol,
        umbral_desde: Number(bonoData.umbral_desde),
        monto: Number(bonoData.monto),
      };

      // Solo agregar ciudad si existe
      if (bonoData.ciudad !== null && bonoData.ciudad !== undefined && bonoData.ciudad !== '') {
        payload.ciudad = bonoData.ciudad;
      }

      console.log('Creating bono with data:', payload);

      const url = `${urlBackend}api/v1/comisiones/bonos/`;
      const resp = await post<BonoEscalon>(url, payload, true, true);
      return extractPayload<BonoEscalon>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'bonos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Bono creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateBono = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...bono }: BonoEscalon) => {
      const url = `${urlBackend}api/v1/comisiones/bonos/${id}/`;
      const resp = await put<BonoEscalon>(url, bono, true, true);
      return extractPayload<BonoEscalon>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'bonos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Bono actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteBono = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/bonos/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'bonos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Bono eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== VALES ====================
export const useFetchVales = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'vales'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/vales/`;
      const resp = await get<any>(url, true);
      const vales = extractList<ValeRegla>(resp);
      return vales.map((vale) => ({
        ...vale,
        umbral: Number(vale.umbral ?? 0),
        monto: Number(vale.monto ?? 0),
      }));
    },
    enabled,
  });
};

export const useCreateVale = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (vale: Partial<ValeRegla>) => {
      const { id, ...valeData } = vale;

      // Construir payload explícitamente
      const payload: any = {
        rol: valeData.rol,
        tipo: valeData.tipo,
        umbral: Number(valeData.umbral),
        monto: Number(valeData.monto),
      };

      // Solo agregar ciudad si existe
      if (valeData.ciudad !== null && valeData.ciudad !== undefined && valeData.ciudad !== '') {
        payload.ciudad = valeData.ciudad;
      }

      const url = `${urlBackend}api/v1/comisiones/vales/`;
      const resp = await post<ValeRegla>(url, payload, true, true);
      return extractPayload<ValeRegla>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'vales'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Vale creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateVale = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...vale }: ValeRegla) => {
      const url = `${urlBackend}api/v1/comisiones/vales/${id}/`;
      const resp = await put<ValeRegla>(url, vale, true, true);
      return extractPayload<ValeRegla>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'vales'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Vale actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteVale = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/vales/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'vales'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Vale eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== CHARGEBACK CONFIG ====================
export const useFetchChargebackConfig = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'chargeback-config'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/chargeback-config/`;
      const resp = await get<any>(url, true);
      // Para chargeback config, obtenemos el primer resultado
      const configs = extractList<ChargebackConfig>(resp);
      if (configs.length > 0) {
        return configs[0];
      }
      return extractPayload<ChargebackConfig | null>(resp) ?? null;
    },
    enabled,
  });
};

export const useUpdateChargebackConfig = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (config: ChargebackConfig) => {
      // Si tiene ID, actualizar con PUT. Si no, crear con POST
      if (config.id) {
        const url = `${urlBackend}api/v1/comisiones/chargeback-config/${config.id}/`;
        const resp = await put<ChargebackConfig>(url, config, true, true);
        return extractPayload<ChargebackConfig>(resp);
      } else {
        const url = `${urlBackend}api/v1/comisiones/chargeback-config/`;
        const resp = await post<ChargebackConfig>(url, config, true, true);
        return extractPayload<ChargebackConfig>(resp);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'chargeback-config'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Configuración de chargeback actualizada', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== PARAMETROS (TODOS) ====================
export const useFetchParametros = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'parametros'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/parametros/`;
      const resp = await get<ParametrosComision>(url, true);
      return extractPayload<ParametrosComision>(resp);
    },
    enabled,
  });
};

// ==================== AUDITORÍA ====================
export const useFetchAuditoria = (filters?: { tabla?: string; obj_id?: number }, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'auditoria', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.tabla) params.append('tabla', filters.tabla);
      if (filters?.obj_id) params.append('obj_id', filters.obj_id.toString());

      const url = `${urlBackend}api/v1/comisiones/auditoria/?${params.toString()}`;
      const resp = await get<ParametroChangeLog[]>(url, true);
      return extractList<ParametroChangeLog>(resp);
    },
    enabled,
  });
};

// ==================== COMMISSION RUNS ====================
export const useCalcularComisiones = (params?: UseMutationsParams & { disableGlobalLoading?: boolean }) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: async (filtros: FiltrosComisionAsesor) => {
      if (!params?.disableGlobalLoading) {
        setIsGlobalLoading(true);
      }
      const url = `${urlBackend}api/v1/comisiones/runs/`;

      // Filtrar valores nulos/undefined de canales
      const canalesLimpios = filtros.canales?.filter((canal) => canal != null && canal !== '');

      const payload = {
        fecha_inicio: filtros.fecha_inicio,
        fecha_fin: filtros.fecha_fin,
        canales: canalesLimpios && canalesLimpios.length > 0 ? canalesLimpios : undefined,
        etiquetas: {
          ciudad: filtros.ciudad,
          asesor_id: filtros.asesor_id,
        },
      };
      const resp = await post<any>(url, payload, true, true);
      return extractPayload<any>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'runs'] });
      params?.enableSuccessAlert &&
        setAlert(true, 'Comisiones calculadas correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params?.customMessageErrorAlert),
    onSettled: () => {
      if (!params?.disableGlobalLoading) {
        setIsGlobalLoading(false);
      }
    },
  });
};

// Consultar estado de tarea Celery
export const useFetchTaskStatus = (taskId: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'task-status', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID is required');
      const url = `${urlBackend}api/v1/comisiones/runs/task-status/${taskId}/`;
      const resp = await get<TaskStatusResponse>(url, true);
      return extractPayload<TaskStatusResponse>(resp);
    },
    enabled: enabled && !!taskId,
    refetchInterval: (query) => {
      // Polling cada segundo mientras está en progreso
      const taskStatus = query.state.data;
      if (taskStatus?.state === 'PENDING' || taskStatus?.state === 'PROGRESS') {
        return 1000;
      }
      return false; // Detener polling cuando termina
    },
    retry: false,
  });
};

export const useFetchCommissionRun = (runId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'runs', runId],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/runs/${runId}/`;
      const resp = await get<CommissionRun>(url, true);
      return extractPayload<CommissionRun>(resp);
    },
    enabled: enabled && !!runId,
  });
};

export const useFetchReporteAsesor = (runId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'reporte-asesor', runId],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/reporte/asesor/?run_id=${runId}`;
      const resp = await get<ComisionAsesorRow[]>(url, true);
      return extractList<ComisionAsesorRow>(resp);
    },
    enabled: enabled && !!runId,
  });
};

// ==================== EXPORT ====================
export const useExportarComisiones = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ run_id, formato, incluir_asesores, incluir_supervisores, incluir_jefatura, incluir_lider }: ExportRequest) => {
      const params = new URLSearchParams({ formato });

      if (incluir_asesores !== undefined) params.append('incluir_asesores', incluir_asesores.toString());
      if (incluir_supervisores !== undefined) params.append('incluir_supervisores', incluir_supervisores.toString());
      if (incluir_jefatura !== undefined) params.append('incluir_jefatura', incluir_jefatura.toString());
      if (incluir_lider !== undefined) params.append('incluir_lider', incluir_lider.toString());

      const url = `${urlBackend}api/v1/comisiones/runs/${run_id}/export/?${params.toString()}`;
      const resp = await get<Blob>(url, true, false);

      // Crear descarga del archivo
      const blobData = extractPayload<Blob>(resp);
      const blob = new Blob([blobData], {
        type: formato === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_comisiones_${run_id}.${formato}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return blobData;
    },
    onSuccess: () => {
      setAlert(true, 'Reporte exportado correctamente', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== CHARGEBACKS ====================
export const useImportChargebacks = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: async (file: File) => {
      setIsGlobalLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = `${urlBackend}api/v1/comisiones/chargebacks/import/`;
      const resp = await post<{ imported: number; errors: string[] }>(url, formData, true, false);
      return extractPayload<{ imported: number; errors: string[] }>(resp);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'chargebacks'] });
      setAlert(
        true,
        `${data.imported} chargebacks importados${data.errors.length > 0 ? ` (${data.errors.length} errores)` : ''}`,
        data.errors.length > 0 ? AlertSeverityEnum.warning : AlertSeverityEnum.success
      );
    },
    onError: error => handleAxiosError(error),
    onSettled: () => setIsGlobalLoading(false),
  });
};

export const useFetchChargebacks = (filters?: { desde?: string; hasta?: string; asesor_id?: number }, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'chargebacks', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.desde) params.append('desde', filters.desde);
      if (filters?.hasta) params.append('hasta', filters.hasta);
      if (filters?.asesor_id) params.append('asesor_id', filters.asesor_id.toString());

      const url = `${urlBackend}api/v1/comisiones/chargebacks/?${params.toString()}`;
      const resp = await get<ChargebackEvent[]>(url, true);
      return extractList<ChargebackEvent>(resp);
    },
    enabled,
  });
};

// ================ AUDITORÍA ================

export const useFetchAuditoriaLogs = (filters?: { tabla?: string; obj_id?: number; campo?: string }, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'auditoria', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.tabla) params.append('tabla', filters.tabla);
      if (filters?.obj_id) params.append('obj_id', filters.obj_id.toString());
      if (filters?.campo) params.append('campo', filters.campo);

      const url = `${urlBackend}api/v1/comisiones/auditoria/?${params.toString()}`;
      const resp = await get<ParametroChangeLog[]>(url, true);
      return extractList<ParametroChangeLog>(resp);
    },
    enabled,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
    refetchIntervalInBackground: false, // Don't refetch when tab is not visible (optimization)
  });
};

// ==================== JEFATURA ====================
export const useFetchJefaturaTramos = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'jefatura-tramos'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/jefatura-tramos/`;
      const resp = await get<JefaturaTramo[]>(url, true);
      return extractList<JefaturaTramo>(resp);
    },
    enabled,
  });
};

export const useCreateJefaturaTramo = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (tramo: Partial<JefaturaTramo>) => {
      const { id, ...tramoData } = tramo;
      const url = `${urlBackend}api/v1/comisiones/jefatura-tramos/`;
      const resp = await post<JefaturaTramo>(url, tramoData, true, true);
      return extractPayload<JefaturaTramo>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'jefatura-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Tramo de jefatura creado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateJefaturaTramo = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...tramo }: JefaturaTramo) => {
      const url = `${urlBackend}api/v1/comisiones/jefatura-tramos/${id}/`;
      const resp = await put<JefaturaTramo>(url, tramo, true, true);
      return extractPayload<JefaturaTramo>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'jefatura-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Tramo de jefatura actualizado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteJefaturaTramo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/jefatura-tramos/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'jefatura-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Tramo de jefatura eliminado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useFetchJefaturaConfig = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'jefatura-config'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/jefatura-config/`;
      const resp = await get<JefaturaConfig[]>(url, true);
      const configs = extractList<JefaturaConfig>(resp);
      return configs.length > 0 ? configs[0] : null;
    },
    enabled,
  });
};

export const useUpdateJefaturaConfig = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (config: JefaturaConfig) => {
      // Si tiene ID, actualizar con PUT. Si no, crear con POST
      if (config.id) {
        const url = `${urlBackend}api/v1/comisiones/jefatura-config/${config.id}/`;
        const resp = await put<JefaturaConfig>(url, config, true, true);
        return extractPayload<JefaturaConfig>(resp);
      } else {
        const url = `${urlBackend}api/v1/comisiones/jefatura-config/`;
        const resp = await post<JefaturaConfig>(url, config, true, true);
        return extractPayload<JefaturaConfig>(resp);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'jefatura-config'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Configuración de jefatura actualizada', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useFetchJefaturaAjusteMeta = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'jefatura-ajuste-meta'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/jefatura-ajuste-meta/`;
      const resp = await get<JefaturaAjusteMeta[]>(url, true);
      const ajustes = extractList<JefaturaAjusteMeta>(resp);
      return ajustes.length > 0 ? ajustes[0] : null;
    },
    enabled,
  });
};

export const useUpdateJefaturaAjusteMeta = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (ajuste: JefaturaAjusteMeta) => {
      // Si tiene ID, actualizar con PUT. Si no, crear con POST
      if (ajuste.id) {
        const url = `${urlBackend}api/v1/comisiones/jefatura-ajuste-meta/${ajuste.id}/`;
        const resp = await put<JefaturaAjusteMeta>(url, ajuste, true, true);
        return extractPayload<JefaturaAjusteMeta>(resp);
      } else {
        const url = `${urlBackend}api/v1/comisiones/jefatura-ajuste-meta/`;
        const resp = await post<JefaturaAjusteMeta>(url, ajuste, true, true);
        return extractPayload<JefaturaAjusteMeta>(resp);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'jefatura-ajuste-meta'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Ajuste de meta actualizado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== LÍDER ====================
export const useFetchLiderTramos = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'lider-tramos'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/lider-tramos/`;
      const resp = await get<LiderTramo[]>(url, true);
      return extractList<LiderTramo>(resp);
    },
    enabled,
  });
};

export const useCreateLiderTramo = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (tramo: Partial<LiderTramo>) => {
      const { id, ...tramoData } = tramo;
      const url = `${urlBackend}api/v1/comisiones/lider-tramos/`;
      const resp = await post<LiderTramo>(url, tramoData, true, true);
      return extractPayload<LiderTramo>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Tramo de líder creado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateLiderTramo = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...tramo }: LiderTramo) => {
      const url = `${urlBackend}api/v1/comisiones/lider-tramos/${id}/`;
      const resp = await put<LiderTramo>(url, tramo, true, true);
      return extractPayload<LiderTramo>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Tramo de líder actualizado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteLiderTramo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/lider-tramos/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-tramos'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Tramo de líder eliminado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useFetchLiderPersonas = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'lider-personas'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/lider-personas/`;
      const resp = await get<LiderPersona[]>(url, true);
      return extractList<LiderPersona>(resp);
    },
    enabled,
  });
};

export const useCreateLiderPersona = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (persona: Partial<LiderPersona>) => {
      const { id, ...personaData } = persona;
      const url = `${urlBackend}api/v1/comisiones/lider-personas/`;
      const resp = await post<LiderPersona>(url, personaData, true, true);
      return extractPayload<LiderPersona>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-personas'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Líder creado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateLiderPersona = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...persona }: LiderPersona) => {
      const url = `${urlBackend}api/v1/comisiones/lider-personas/${id}/`;
      const resp = await put<LiderPersona>(url, persona, true, true);
      return extractPayload<LiderPersona>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-personas'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Líder actualizado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteLiderPersona = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/lider-personas/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-personas'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Líder eliminado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useFetchLiderConfig = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'lider-config'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/lider-config/`;
      const resp = await get<LiderConfig[]>(url, true);
      const configs = extractList<LiderConfig>(resp);
      return configs.length > 0 ? configs[0] : null;
    },
    enabled,
  });
};

export const useUpdateLiderConfig = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (config: LiderConfig) => {
      // Si tiene ID, actualizar con PUT. Si no, crear con POST
      if (config.id) {
        const url = `${urlBackend}api/v1/comisiones/lider-config/${config.id}/`;
        const resp = await put<LiderConfig>(url, config, true, true);
        return extractPayload<LiderConfig>(resp);
      } else {
        const url = `${urlBackend}api/v1/comisiones/lider-config/`;
        const resp = await post<LiderConfig>(url, config, true, true);
        return extractPayload<LiderConfig>(resp);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'lider-config'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Configuración de líder actualizada', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== COORDINADOR ====================
export const useFetchCoordinadorPorcentajes = (enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'coordinador-porcentajes'],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/coordinador-porcentajes/`;
      const resp = await get<CoordinadorPorcentaje[]>(url, true);
      return extractList<CoordinadorPorcentaje>(resp);
    },
    enabled,
  });
};

export const useCreateCoordinadorPorcentaje = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (porcentaje: Partial<CoordinadorPorcentaje>) => {
      const { id, ...porcentajeData } = porcentaje;
      const url = `${urlBackend}api/v1/comisiones/coordinador-porcentajes/`;
      const resp = await post<CoordinadorPorcentaje>(url, porcentajeData, true, true);
      return extractPayload<CoordinadorPorcentaje>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'coordinador-porcentajes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, params.customSuccessMessage ?? 'Porcentaje de coordinador creado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error, params.customMessageErrorAlert),
  });
};

export const useUpdateCoordinadorPorcentaje = (params: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, ...porcentaje }: CoordinadorPorcentaje) => {
      const url = `${urlBackend}api/v1/comisiones/coordinador-porcentajes/${id}/`;
      const resp = await put<CoordinadorPorcentaje>(url, porcentaje, true, true);
      return extractPayload<CoordinadorPorcentaje>(resp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'coordinador-porcentajes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      params.enableSuccessAlert &&
        setAlert(true, 'Porcentaje de coordinador actualizado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

export const useDeleteCoordinadorPorcentaje = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${urlBackend}api/v1/comisiones/coordinador-porcentajes/${id}/`;
      await remove(url, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'coordinador-porcentajes'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'parametros'] });
      queryClient.invalidateQueries({ queryKey: ['comisiones', 'auditoria'] });
      setAlert(true, 'Porcentaje de coordinador eliminado', AlertSeverityEnum.success);
    },
    onError: error => handleAxiosError(error),
  });
};

// ==================== REPORTES JEFATURA, LÍDER Y COORDINADOR ====================
export const useFetchReporteJefatura = (runId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'reporte-jefatura', runId],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/reporte/jefatura/?run_id=${runId}`;
      const resp = await get<ComisionJefaturaRow[]>(url, true);
      return extractList<ComisionJefaturaRow>(resp);
    },
    enabled: enabled && !!runId,
  });
};

export const useFetchReporteLider = (runId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'reporte-lider', runId],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/reporte/lider/?run_id=${runId}`;
      const resp = await get<ComisionLiderRow[]>(url, true);
      return extractList<ComisionLiderRow>(resp);
    },
    enabled: enabled && !!runId,
  });
};

export const useFetchReporteCoordinador = (runId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['comisiones', 'reporte-coordinador', runId],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/coordinador/?run_id=${runId}`;
      console.log('🔍 Fetching coordinador data with URL:', url);
      const resp = await get<ComisionCoordinadorRow[]>(url, true);
      console.log('📦 Raw response from coordinador endpoint:', resp);
      const result = extractList<ComisionCoordinadorRow>(resp);
      console.log('✅ Extracted coordinador data:', result);
      return result;
    },
    enabled: enabled && !!runId,
  });
};

// ==================== CONTRATOS CHARGEBACK ====================
export const useFetchContratosChargeback = (
  params: { nombre_empleado: string; fecha_hasta: string },
  enabled = true
) => {
  return useQuery({
    queryKey: ['comisiones', 'contratos-chargeback', params],
    queryFn: async () => {
      const url = `${urlBackend}api/v1/comisiones/contratos-chargeback/`;
      const resp = await post<ContratoChargeback[]>(url, params, true, true);
      return extractList<ContratoChargeback>(resp);
    },
    enabled: enabled && !!params.nombre_empleado && !!params.fecha_hasta,
  });
};

/**
 * Hook para registrar chargebacks históricos de un run.
 * Esto evita que los chargebacks se descuenten dos veces en períodos consecutivos.
 */
export const useRegistrarChargebacksHistoricos = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (run_id: number) => {
      const url = `${urlBackend}api/v1/comisiones/registrar-chargebacks/`;
      const resp = await post<{
        success: boolean;
        message: string;
        registros_creados: number;
        run_id: number;
        periodo: string;
      }>(url, { run_id }, true, true);
      return extractPayload(resp);
    },
    onSuccess: (data: any) => {
      setAlert(
        true,
        data?.message || `Se registraron ${data?.registros_creados || 0} chargebacks exitosamente`,
        AlertSeverityEnum.success
      );
    },
    onError: (error: any) => {
      // Manejar errores específicos del endpoint
      const errorData = error?.response?.data;

      if (errorData?.error) {
        const errorMsg = errorData.error;

        // Error de duplicado - ya registrado
        if (errorMsg.includes('ya fueron registrados') || errorMsg.includes('Ya existen chargebacks')) {
          const periodo = errorData.periodo || '';
          const sugerencia = errorData.sugerencia || '';

          setAlert(
            true,
            `${errorMsg}${periodo ? ` (${periodo})` : ''}${sugerencia ? `. ${sugerencia}` : ''}`,
            AlertSeverityEnum.warning
          );
        } else {
          // Otros errores
          setAlert(
            true,
            `Error al registrar chargebacks: ${errorMsg}`,
            AlertSeverityEnum.error
          );
        }
      } else {
        // Fallback para errores no estructurados
        handleAxiosError(error);
      }
    },
  });
};
